import { createContext, type ComponentType, type ReactNode } from 'react';

import type { LimuloQueue, LimuloQueueTicket } from '@/lib/limulo/code';
import type { AttesaProfili, PianoIngressoCoda } from '@/lib/limulo/registration';

/**
 * LA VISTA DELLA CARD «SEI IN CODA» che `CodeTorneiPanel` (`InAttesa`) passa a una pelle alternativa, quando qualcuno sopra di lui ne ha messa una nel Context. Porta SOLO valori che il pannello ha gia' calcolato e il gesto di oggi:
 * nessun verdetto nuovo, nessun orologio nuovo.
 *
 * @param nomi i nomi delle code in cui si aspetta (quelli che il pannello dice oggi).
 * @param da l'istante piu' vecchio fra le attese (`waitingSinceOf`), o `null` quando il server non lo dice: allora il tempo NON si mostra.
 * @param adesso l'orologio del pannello (un secondo, solo testo): la vista legge `adesso - da`, non ne accende un altro.
 * @param secondi dopo quanti secondi di app chiusa si esce dalla coda (`expiresAfterMs`), o `null` quando il server non lo dice.
 * @param busy una scrittura e' in volo.
 * @param onLeave il gesto di oggi: esce da TUTTO il biglietto del gioco.
 */
export interface VistaInAttesa {
  nomi: string[];
  da: number | null;
  adesso: number;
  secondi: number | null;
  busy: boolean;
  onLeave: () => void;
}

/**
 * LA VISTA DELL'ELENCO delle code che `CodeTorneiPanel` (`Elenco`) passa a una pelle alternativa. Anche qui solo valori gia' calcolati dal pannello e i suoi gesti.
 *
 * @param queues le sole code 1v1 (`queuesOfKind(…, 'coda')`): possono essere zero col bottone «tutte» acceso.
 * @param tutte «Entra in tutte le code» si offre (`tutteLeCodeSiOffrono`).
 * @param spento il bottone e le righe sono spenti: una scrittura in volo, o i profili in lettura / illeggibili.
 * @param statoNota la frase dei profili in lettura o illeggibili, o `null`: allora la nota e' quella di «tutte».
 * @param game il nome del gioco che la nota dice (`{{game}}`).
 * @param vuoto il nodo di oggi per «nessuna coda 1v1»: la vista lo mette dove le serve.
 * @param entra il gesto di oggi, col profilo scelto come all'iscrizione: aptica e `onJoin({ gameId, …, profileId })`.
 */
export interface VistaElencoCode {
  queues: LimuloQueue[];
  tutte: boolean;
  busy: boolean;
  spento: boolean;
  statoNota: string | null;
  game: string;
  vuoto: ReactNode;
  entra: (input: { queueId: string } | { anyMode: true }) => void;
}

/**
 * LA VISTA DELL'ELENCO DEI SIT&GO che `SitngoCodePanel` (`ElencoSitngo`) passa a una pelle alternativa. Solo valori gia' calcolati dal pannello e i suoi gesti: l'ingresso col profilo implicito (la creazione, il sync atteso, la guardia contro il doppio tocco) resta TUTTO in `entra`, quello di oggi,
 * e la pelle non importa niente dei profili.
 *
 * @param queues i soli sit&go del gioco (`queuesOfKind(…, 'sit-and-go')`): possono essere zero col bottone «tutte» acceso.
 * @param ticket il biglietto del gioco, o `null`: «sono in coda qui» e` `waitingInQueue(ticket, queueId)`, la chiamata di oggi.
 * @param tutte «Entra in tutte le code» si offre (`tutteLeCodeSiOffrono`).
 * @param busy una scrittura e` in volo (spegne «Esci», come oggi).
 * @param preparando l'ingresso sta preparando il profilo (creazione e sync in corso).
 * @param entraSpento il bottone «Entra» e` spento: scrittura in volo, profili in lettura o illeggibili, preparazione in corso, schema in lettura.
 * @param nota la frase dei profili in lettura o illeggibili, o `null`: allora la nota e` quella di «tutte».
 * @param attesaSchema lo schema del profilo si sta leggendo (o non si e` riusciti): la riga che lo dice.
 * @param piano il piano d'ingresso di oggi (`pianoIngressoCoda`): con `crea-profilo` si dice, una volta, che il profilo lo crea l'app; con `illeggibile` c'e` «Riprova».
 * @param stato lo stato della lettura dei profili (`attesaProfili`).
 * @param esito l'esito del profilo creato da «Entra» (la chiave `register.implicitProfile.*` e i suoi valori), o `null`.
 * @param vuoto il nodo di oggi per «nessun sit&go»: la pelle lo mette dove le serve.
 * @param game il nome del gioco che la nota dice (`{{game}}`).
 * @param entra il gesto di oggi, con tutto il suo percorso: aptica, profilo implicito, sync atteso, `onJoin`.
 * @param riprovaSchema rilegge lo schema del profilo (il «Riprova» di oggi).
 * @param onLeave il gesto di oggi: esce da QUEL `queueId`.
 */
export interface VistaElencoSitngo {
  queues: LimuloQueue[];
  ticket: LimuloQueueTicket | null;
  tutte: boolean;
  busy: boolean;
  preparando: boolean;
  entraSpento: boolean;
  nota: string | null;
  attesaSchema: boolean;
  piano: PianoIngressoCoda;
  stato: AttesaProfili;
  esito: { key: string; values?: Record<string, string> } | null;
  vuoto: ReactNode;
  game: string;
  entra: (target: { queueId: string } | { anyMode: true }) => void;
  riprovaSchema: () => void;
  onLeave: (queueId: string) => void;
}

/**
 * LE PELLI ALTERNATIVE dei pezzi dei pannelli delle code che hanno logica dentro di se'. Ognuna e' un componente DI MODULO: un valore creato nel render cambierebbe identita' a ogni polling della lista e smonterebbe la vista.
 * La famiglia code ha un Context solo, perche' i pannelli che la leggono mostrano la stessa card in coda: `InAttesa` serve la card di `CodeTorneiPanel` e quella (`AttesaCompatta`) di `SitngoCodePanel`, che hanno la stessa vista.
 */
export interface PelleCode {
  InAttesa?: ComponentType<{ vista: VistaInAttesa }>;
  Elenco?: ComponentType<{ vista: VistaElencoCode }>;
  ElencoSitngo?: ComponentType<{ vista: VistaElencoSitngo }>;
}

/**
 * IL PUNTO D'INNESTO di una pelle alternativa delle code (stesso schema di `SlotLiveHub` e `SlotPannelloIncontro`). Il valore di partenza e' `null`: senza nessuno sopra, i pannelli rendono quello di sempre e il DOM non cambia di un nodo.
 * ⛔ Il valore di partenza resta `null`: una pelle di default farebbe cambiare il pannello per chiunque, anche dove nessuno l'ha scelta.
 */
export const SlotPelleCode = createContext<PelleCode | null>(null);
